import { Drawer } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Table } from 'react-bootstrap';
import MuiAlert from '@mui/material/Alert';
import { useDispatch, useSelector } from 'react-redux';
import {
  createGrnForVehicleTracking,
  getStorageLocations,
  getVehicleTrackingLineItems,
} from 'src/redux/Actions/VehicleTracking';

export default function CreateGRN({
  openGRNDrawer,
  setOpenGRNDrawer,
  selectedVehicle,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const vehicleTrackingLineItemData =
    vehicleTrackingReducer?.vehicleTrackingLineItemList;
  const storageLocationsGroupedByPlant =
    vehicleTrackingReducer?.storageLocationsGroupedByPlant;
  const [formData, setFormData] = useState({});
  const [invalidFormSubmission, setInvalidFormSubmission] = useState(false);

  const handleAlertClose = () => setInvalidFormSubmission(false);
  function getStorageLocationsByPlantId(dataObj, plantId) {
    const plant = dataObj.find((item) => item.plant_id === plantId);
    return plant ? plant.storage_locations : [];
  }

  useEffect(() => {
    if (selectedVehicle && openGRNDrawer) {
      dispatch(
        getVehicleTrackingLineItems(tenantOrganisationId, selectedVehicle),
      );
    }
  }, [dispatch, selectedVehicle, openGRNDrawer]);

  useEffect(() => {
    const plantIds = Array.from(
      new Set(
        (vehicleTrackingLineItemData || [])
          .map((item) => item?.plant?.id)
          .filter(Boolean),
      ),
    );
    if (plantIds.length > 0) {
      dispatch(getStorageLocations(tenantOrganisationId, plantIds));
    }
  }, [vehicleTrackingLineItemData]);

  useEffect(() => {
    if (!openGRNDrawer) {
      setFormData({});
      setInvalidFormSubmission(false);
    }
  }, [openGRNDrawer]);

  const validateFormData = () => {
    const hasInvalidEntry = Object.values(formData).some(
      (dataObj) => dataObj.active && !dataObj.storage_location_id,
    );

    if (hasInvalidEntry) {
      setInvalidFormSubmission(true);
      return false;
    }
    return true;
  };
  const handleCheckboxClick = (itemObj) => {
    setFormData((prevData) => {
      const existing = prevData[itemObj.id] || {
        purchase_order_line_item_id: itemObj.purchase_order_line_item_id,
        receiving_quantity: itemObj.receiving_quantity,
        plant_id: itemObj.plant.id,
        storage_location_id: null,
        batch_number: null,
      };
      return {
        ...prevData,
        [itemObj.id]: {
          ...existing,
          active: !prevData[itemObj.id]?.active,
        },
      };
    });
  };
  function getStorageLocationOptionsForPlant(plantId) {
    const storageLocations = getStorageLocationsByPlantId(
      storageLocationsGroupedByPlant,
      plantId,
    );
    return storageLocations.map((location) => ({
      value: location.id,
      label: `${location.sap_code} - ${location.name}`,
    }));
  }

  const handleOnChange = (e, id) => {
    const { name, value } = e.target;

    const item = vehicleTrackingLineItemData.find((i) => i.id === id);
    if (!item) return;

    setFormData((prevData) => {
      const existing = prevData[id] || {
        purchase_order_line_item_id: item.purchase_order_line_item_id,
        receiving_quantity: item.receiving_quantity,
        plant_id: item.plant.id,
      };

      return {
        ...prevData,
        [id]: {
          ...existing,
          [name]: value,
          active: true,
        },
      };
    });
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    const activeItems = Object.values(formData)
      .filter((dataObj) => dataObj.active)
      .map((dataObj) => ({
        purchase_order_line_item_id: dataObj.purchase_order_line_item_id,
        receiving_quantity: dataObj.receiving_quantity,
        plant_id: dataObj.plant_id,
        storage_location_id: dataObj.storage_location_id,
        batch_number: dataObj.batch_number,
        header_text: dataObj.header_text,
        bill_of_lading: dataObj.bill_of_lading,
        reference_document_number: dataObj.reference_document_number,
      }));

    if (activeItems.length === 0) {
      setInvalidFormSubmission(true);
      return;
    }

    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      vehicle_tracking_number: selectedVehicle,
      items: activeItems,
    };

    dispatch(createGrnForVehicleTracking(payload)).then(() => {
      window.location.reload();
    });
  };
  return (
    <Drawer
      anchor="right"
      open={Boolean(openGRNDrawer)}
      onClose={() => setOpenGRNDrawer(false)}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: 1 }}
      PaperProps={{
        sx: { width: '80%' },
      }}
    >
      <div className="grn-modal-container">
        <div className="grn-modal-header">
          <span className="text-xxl text-bold">
            Goods Receipt: Select Line Items & Storage
          </span>
        </div>
        <div className="grn-modal-body">
          {invalidFormSubmission && (
            <MuiAlert
              onClose={handleAlertClose}
              severity="error"
              elevation={6}
              variant="filled"
            >
              Please select a Storage Location for all selected items.
            </MuiAlert>
          )}
          <Table hover responsive className="grn-custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Plant</th>
                <th>Purchase Order</th>
                <th>PO Line Item</th>
                <th>Quantity</th>
                <th>Storage Location</th>
                <th>Batch</th>
                <th>Header Text</th>
                <th>Bill of Lading</th>
                <th>Ref Doc</th>
              </tr>
            </thead>
            <tbody>
              {vehicleTrackingLineItemData?.map((itemData) => (
                <tr key={itemData?.material_code}>
                  <td>
                    <input
                      type="checkbox"
                      checked={formData[itemData.id]?.active || false}
                      onChange={() => handleCheckboxClick(itemData)}
                    />
                  </td>
                  <td>{itemData?.plant.plant_code}</td>
                  <td>{itemData?.purchase_order_number}</td>
                  <td>{itemData?.material_code}</td>
                  <td>
                    {itemData?.receiving_quantity}{' '}
                    {itemData?.unit_of_measurement}
                  </td>
                  <td>
                    <Select
                      className="react-select-container"
                      isClearable
                      placeholder="Select Storage Location"
                      onChange={(selectedOption) =>
                        handleOnChange(
                          {
                            target: {
                              name: 'storage_location_id',
                              value: selectedOption?.value ?? '',
                            },
                          },
                          itemData.id,
                        )
                      }
                      options={getStorageLocationOptionsForPlant(
                        itemData.plant.id,
                      )}
                    />
                  </td>
                  <td>
                    <input
                      name="batch_number"
                      className="form-input-section"
                      size="small"
                      type="text"
                      value={formData[itemData.id]?.batch_number || ''}
                      onChange={(e) => handleOnChange(e, itemData.id)}
                    />
                  </td>
                  <td>
                    <input
                      name="header_text"
                      className="form-input-section"
                      size="small"
                      type="text"
                      value={formData[itemData.id]?.header_text || ''}
                      onChange={(e) => handleOnChange(e, itemData.id)}
                    />
                  </td>
                  <td>
                    <input
                      name="bill_of_lading"
                      className="form-input-section"
                      size="small"
                      type="text"
                      value={formData[itemData.id]?.bill_of_lading || ''}
                      onChange={(e) => handleOnChange(e, itemData.id)}
                    />
                  </td>
                  <td>
                    <input
                      name="reference_document_number"
                      className="form-input-section"
                      size="small"
                      type="text"
                      value={
                        formData[itemData.id]?.reference_document_number || ''
                      }
                      onChange={(e) => handleOnChange(e, itemData.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="grn-modal-footer">
          <button className="btn-sm btn-black" onClick={handleSubmit}>
            Submit GRN
          </button>
        </div>
      </div>
    </Drawer>
  );
}
